import Joi from "joi";

export const tenantInfoSchema = Joi.object({
  userName: Joi.string()
    .min(3)
    .regex(/^[a-zA-Z0-9\s]+$/)
    .required()
    .messages({
      "string.base": "User Name must be a valid text.",
      "string.empty": "User Name is required.",
      "string.min": "User Name must be at least {#limit} characters long.",
      "string.pattern.base": "User Name cannot contain special characters.",
      "any.required": "User Name is required.",
    }),

  email: Joi.string()
    .email({ tlds: { allow: false } })
    .required()
    .messages({
      "string.email": "Please enter a valid email address.",
      "string.empty": "Email is required.",
      "any.required": "Email is required.",
    }),

  npType: Joi.string().required().messages({
    "string.empty": "NP Type is required",
  }),

  npDomain: Joi.string().required().messages({
    "string.empty": "NP Domain is required",
  }),
  msnFee: Joi.when("npDomain", {
    is: "Retail - MSN Provider",
    then: Joi.object({
      type: Joi.string().required().messages({
        "string.empty": "Please select a Fee Type",
        "any.required": "Fee Type is required",
      }),
      amount: Joi.number().min(0).required().messages({
        "number.base": "Fee Amount must be a valid number",
        "number.min": "Fee Amount cannot be negative",
        "any.required": "Fee Amount is required",
      }),
      valueType: Joi.string().valid("percent", "fixed").required(),
    }).required(),
    otherwise: Joi.object({
      type: Joi.string().allow(""),
      amount: Joi.any().allow(""),
      valueType: Joi.string().allow(""),
    }).optional(),
  }),

  stages: Joi.string().required().messages({
    "string.empty": "Stage is required",
    "any.required": "Stage is required",
  }),
  nokey: Joi.string().optional().allow("").messages({
    "string.base": "No Keys must be a valid text",
  }),

  hasSellerApp: Joi.when("npType", {
    is: "Buyer App",
    then: Joi.optional(),
    otherwise: Joi.when("npDomain", {
      is: Joi.valid("Retail - ISN", "Retail - MSN"),
      then: Joi.string().valid("Yes", "No").required().messages({
        "any.required": "Please select if Seller App is live",
        "any.only": "Please select either Yes or No for Seller App live status",
      }),
      otherwise: Joi.optional(),
    }),
  }),

  // ✅ --- START: CORRECTED LOGIC ---
  withSellerApp: Joi.when("npType", {
    is: "Buyer App",
    then: Joi.optional(),
    otherwise: Joi.when("npDomain", {
      is: Joi.valid("Retail - ISN", "Retail - MSN"),
      then: Joi.when("hasSellerApp", {
        is: "No",
        then: Joi.string().valid("Yes", "No").required().messages({
          "any.required": "Please specify whether to include Seller App",
          "any.only": "Invalid value for Seller App inclusion",
        }),
        otherwise: Joi.optional(), // Optional if hasSellerApp is "Yes"
      }),
      otherwise: Joi.optional(), // Optional if npDomain is not Retail...
    }),
  }),

  retailDomains: Joi.array().items(Joi.string()).min(1).required().messages({
    "array.min": "Select at least one Seller Retail Domain",
    "any.required": "Seller Retail Domains are required",
  }),

  services: Joi.when("npDomain", {
    is: "Retail - MSN Provider",
    then: Joi.array().required().messages({
      "any.required": "Services are required",
    }),
    otherwise: Joi.when("hasSellerApp", {
      is: Joi.valid("Yes"),
      then: Joi.array().min(1).required().messages({
        "array.min": "At least one service must be selected",
        "any.required": "At least one service must be selected",
      }),
      otherwise: Joi.array().optional().messages({
        // Optional when hasSellerApp is "No" or empty
      }),
    }),
  }),

  shiprocketEmail: Joi.string()
    .email({ tlds: { allow: false } })
    .optional()
    .allow("")
    .messages({
      "string.email": "Please enter a valid Shiprocket Email",
    }),

  shiprocketPassword: Joi.string().min(4).optional().allow("").messages({
    "string.min": "Shiprocket Password must be at least 4 characters",
  }),
  // ✅ --- END: CORRECTED LOGIC ---

  nimbusEmail: Joi.string()
    .email({ tlds: { allow: false } })
    .optional()
    .allow("")
    .messages({
      "string.email": "Please enter a valid Nimbus Email",
    }),
  nimbusPassword: Joi.string().min(4).optional().allow("").messages({
    "string.min": "Nimbus Password must be at least 4 characters",
  }),
  webhook: Joi.when("services", {
    is: Joi.array().has("RSF"),
    then: Joi.string().uri().required().messages({
      "string.uri": "Webhook URL must be a valid URL",
      "string.empty": "NP Communication Webhook is required",
      "any.required": "NP Communication Webhook is required",
    }),
    otherwise: Joi.string().uri().allow("").optional().messages({
      "string.uri": "Webhook URL must be a valid URL",
    }),
  }),

  npId: Joi.string().required().messages({
    "string.empty": "Subscriber Id is required",
  }),

  npURL: Joi.string().uri().required().messages({
    "string.empty": "Subscriber URL is required",
    "string.uri": "Subscriber URL must be a valid URL",
  }),

  settlement_agency_public_key: Joi.when("services", {
    is: Joi.array().has("RSF"),
    then: Joi.string().required().messages({
      "string.empty": "Settlement Agency Public Key is required",
      "any.required": "Settlement Agency Public Key is required",
    }),
    otherwise: Joi.string().allow("").optional().messages({
      "string.base": "Settlement Agency Public Key must be a valid text",
    }),
  }),

  np_subscriber_private_id: Joi.when("services", {
    is: Joi.array().has("RSF"),
    then: Joi.string().required().messages({
      "string.empty": "Subscriber Private Id is required",
      "any.required": "Subscriber Private Id is required",
    }),
    otherwise: Joi.string().allow("").optional().messages({
      "string.base": "Subscriber Private Id must be a valid text",
    }),
  }),

  np_unique_id: Joi.when("services", {
    is: Joi.array().has("RSF"),
    then: Joi.string().required().messages({
      "string.empty": "Unique Id is required",
      "any.required": "Unique Id is required",
    }),
    otherwise: Joi.string().allow("").optional().messages({
      "string.base": "Unique Id must be a valid text",
    }),
  }),

  np_signing_public_id: Joi.when("services", {
    is: Joi.array().has("RSF"),
    then: Joi.string().required().messages({
      "string.empty": "Signing Public Id is required",
      "any.required": "Signing Public Id is required",
    }),
    otherwise: Joi.string().allow("").optional().messages({
      "string.base": "Signing Public Id must be a valid text",
    }),
  }),

  customer_support: Joi.when("services", {
    is: Joi.array().has("IGM"), // Use .has("IGM")
    then: Joi.object({
      name: Joi.string().min(3).required().messages({
        "string.empty": "Customer Support Name is required.",
        "string.min": "Customer Support Name must be at least 3 characters.",
      }),
      email: Joi.string()
        .email({ tlds: { allow: false } })
        .required()
        .messages({
          "string.empty": "Customer Support Email is required.",
          "string.email": "Please enter a valid Customer Support Email.",
        }),
      phone: Joi.string()
        .length(10)
        .pattern(/^[0-9]+$/)
        .required()
        .messages({
          "string.empty": "Customer Support Phone is required.",
          "string.length": "Customer Support Phone must be exactly 10 digits.",
          "string.pattern.base":
            "Customer Support Phone must contain only digits.",
        }),
    }).required(),
    otherwise: Joi.object({
      name: Joi.string().allow(""),
      email: Joi.string().allow(""),
      phone: Joi.string().allow(""),
    }).optional(),
  }),

  gro_support: Joi.when("services", {
    is: Joi.array().has("IGM"), // Use .has("IGM")
    then: Joi.object({
      name: Joi.string().min(3).required().messages({
        "string.empty": "GRO Support Name is required.",
        "string.min": "GRO Support Name must be at least 3 characters.",
      }),
      email: Joi.string()
        .email({ tlds: { allow: false } })
        .required()
        .messages({
          "string.empty": "GRO Support Email is required.",
          "string.email": "Please enter a valid GRO Support Email.",
        }),
      phone: Joi.string()
        .length(10)
        .pattern(/^[0-9]+$/)
        .required()
        .messages({
          "string.empty": "GRO Support Phone is required.",
          "string.length": "GRO Support Phone must be exactly 10 digits.",
          "string.pattern.base": "GRO Support Phone must contain only digits.",
        }),
    }).required(),
    otherwise: Joi.object({
      name: Joi.string().allow(""),
      email: Joi.string().allow(""),
      phone: Joi.string().allow(""),
    }).optional(),
  }),

  checklist: Joi.when("stages", {
    is: Joi.string().required(),
    then: Joi.alternatives()
      .try(
        Joi.object(null).allow(null), // Allow null if API doesn't provide checklist items
        Joi.object()
          .pattern(Joi.string(), Joi.boolean())
          .custom((value, helpers) => {
            // If checklist exists as an object, require at least one item selected
            if (value === null) return value;
            const selectedCount = Object.values(value || {}).filter(
              Boolean,
            ).length;
            if (selectedCount === 0) {
              return helpers.error("any.custom");
            }
            return value;
          })
          .messages({
            "any.custom": "Please select at least one checklist item",
          }),
      )
      .required(),
    otherwise: Joi.object().optional(),
  }),
});

// ... storeSetupSchema (no changes needed) ...

export const storeSetupSchema = Joi.object({
  sBrandLogo: Joi.any()
    .custom((file, helpers) => {
      if (!file) return helpers.error("any.required");

      const validFormats = ["image/png", "image/jpeg", "image/jpg"];
      if (!validFormats.includes(file.type)) {
        return helpers.error("file.format");
      }

      if (file.size > 2 * 1024 * 1024) {
        return helpers.error("file.size");
      }

      return file;
    })
    .required()
    .messages({
      "any.required": "Brand Logo is required.",
      "file.format": "Brand Logo must be PNG, JPG, or JPEG.",
      "file.size": "Brand Logo must be under 2MB.",
    }),

  brandName: Joi.string()
    .min(3)
    .regex(/^[a-zA-Z0-9\s]+$/)
    .required()
    .messages({
      "string.base": "Brand Name must be a valid text.",
      "string.empty": "Brand Name is required.",
      "string.min": "Brand Name must be at least {#limit} characters long.",
      "string.pattern.base": "Brand Name cannot contain special characters.",
      "any.required": "Brand Name is required.",
    }),

  storeTypeId: Joi.any().allow(null),

  storeKeys: Joi.when(
    Joi.object({
      storeTypeId: Joi.valid(5), // Custom store
    }).unknown(),
    {
      then: Joi.array().optional().allow(null).default([]),
      otherwise: Joi.when(
        Joi.object({
          npType: Joi.valid("Seller App"), // Only validate if Seller App
        }).unknown(),
        {
          then: Joi.array()
            .items(
              Joi.object({
                key: Joi.string().required(),
                value: Joi.when("key", {
                  switch: [
                    {
                      is: "store_url",
                      then: Joi.string().uri().required().messages({
                        "any.required": "Store URL is required",
                        "string.uri": "Store URL must be a valid URL",
                      }),
                    },
                    {
                      is: "access_token",
                      then: Joi.string().required().messages({
                        "string.empty": "Access Token is required",
                      }),
                    },
                    {
                      is: "admin_access_token",
                      then: Joi.string().required().messages({
                        "string.empty": "Admin Access Token is required",
                      }),
                    },
                  ],
                  otherwise: Joi.string().required().messages({
                    "string.empty": "This field is required",
                  }),
                }),
              }),
            )
            .min(1)
            .required()
            .messages({
              "array.min": "At least one configuration field is required.",
              "any.required": "Store configuration is required.",
            }),
          otherwise: Joi.array().optional().allow(null).default([]), // Skip for non-Seller App
        },
      ),
    },
  ),
});
